import {FileWithPath} from 'react-dropzone';
import {Controller, FormProvider, useForm} from 'react-hook-form';
import {CheckboxElement, TextFieldElement} from 'react-hook-form-mui';
import {useNavigate} from 'react-router-dom';
import {yupResolver} from '@hookform/resolvers/yup';
import {Button, Grid2 as Grid, MenuItem, Stack, TextField} from '@mui/material';
import {isAxiosError} from 'axios';
import {useSnackbar} from 'notistack';
import {boolean, InferType, mixed, object, string} from 'yup';

import {AdminUploadFileType, useUploadAdminFile} from '@entities/common';
import {
  MARKETPLACE_ITEM_TYPES,
  MarketplaceItemType,
  useCreateMarketplaceItem,
} from '@entities/marketplace';
import {getErrorMessageFromApiError} from '@shared/api';
import {
  CardBox,
  imageFileTypes,
  ImageUploadField,
  InputType,
  RhfNumberInput,
} from '@shared/components';
import {
  numberTransformEmptyStringToNull,
  stringDefinedButCanBeEmpty,
} from '@shared/utils';
import {FormRow, Header} from '@widgets/common';

import {COLOR__WHITE} from '@/theme/colors';

const integerFromInputSchema = numberTransformEmptyStringToNull
  .integer()
  .min(0)
  .required();

const v8nSchema = object().shape({
  title: string().required(),
  description: stringDefinedButCanBeEmpty,
  count: integerFromInputSchema,
  type: string().oneOf(MARKETPLACE_ITEM_TYPES).required(),
  price: integerFromInputSchema,
  imageFile: mixed<FileWithPath>().required().nullable(),
  order: integerFromInputSchema,
  isExclusive: boolean().required(),
});

type FormValues = InferType<typeof v8nSchema>;

const getUploadTypeByMarketplaceItemType = (
  type: MarketplaceItemType,
): AdminUploadFileType => {
  switch (type) {
    case MarketplaceItemType.avatar: {
      return 'avatar';
    }
    case MarketplaceItemType.gear: {
      return 'avatar_gear';
    }
    case MarketplaceItemType.icon:
    case MarketplaceItemType.cfp: {
      return 'icon';
    }
  }
};

export const CreateMarketplaceItemPage = () => {
  const {enqueueSnackbar} = useSnackbar();
  const navigate = useNavigate();

  const formMethods = useForm<FormValues>({
    mode: 'onBlur',
    resolver: yupResolver(v8nSchema),
    defaultValues: {
      title: '',
      description: '',
      count: 0,
      type: MarketplaceItemType.avatar,
      price: 0,
      imageFile: null,
      order: 0,
      isExclusive: false,
    },
  });
  const {control, handleSubmit} = formMethods;

  const {mutateAsync: uploadAdminFile, isPending: isUploadingFile} =
    useUploadAdminFile();
  const {mutateAsync: createMarketplaceItem, isPending: isCreatingItem} =
    useCreateMarketplaceItem();

  const onSubmit = async ({
    title,
    description,
    count,
    type,
    price,
    imageFile: imageFileToUpload,
    order,
    isExclusive,
  }: FormValues): Promise<void> => {
    try {
      if (!imageFileToUpload) {
        return;
      }

      const uploadedImage = await uploadAdminFile({
        file: imageFileToUpload,
        type: getUploadTypeByMarketplaceItemType(type),
      });

      await createMarketplaceItem({
        title,
        description,
        count,
        type,
        price,
        imageUrl: uploadedImage.key,
        order,
        isExclusive,
      });

      navigate(-1);
    } catch (error) {
      const errMsg = isAxiosError(error)
        ? getErrorMessageFromApiError(error)
        : '';

      enqueueSnackbar(`Could not create marketplace item. ${errMsg}`, {
        variant: 'error',
      });
    }
  };

  return (
    <>
      <Header title="Create item" mb={1} />

      <CardBox bgcolor={COLOR__WHITE}>
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <FormProvider {...formMethods}>
            <Grid container rowSpacing={1} columnSpacing={4} mt={2}>
              <Grid size={6}>
                <Stack spacing={5}>
                  <TextFieldElement
                    name="title"
                    label="Title"
                    control={control}
                    autoFocus
                    placeholder="title"
                  />

                  <TextFieldElement
                    name="description"
                    label="Description"
                    control={control}
                    placeholder="description"
                    multiline
                    rows={4}
                    InputProps={{
                      inputComponent: 'textarea',
                    }}
                  />

                  <Controller
                    name="type"
                    control={control}
                    render={({field, fieldState}) => (
                      <TextField
                        {...field}
                        select
                        fullWidth
                        label="Type"
                        error={!!fieldState.error}
                        helperText={fieldState.error?.message}
                      >
                        {MARKETPLACE_ITEM_TYPES.map(itemType => (
                          <MenuItem key={itemType} value={itemType}>
                            {itemType}
                          </MenuItem>
                        ))}
                      </TextField>
                    )}
                  />

                  <FormRow>
                    <RhfNumberInput
                      name="price"
                      label="Price"
                      control={control}
                      inputType={InputType.positiveInteger}
                    />

                    <RhfNumberInput
                      name="count"
                      label="Count"
                      control={control}
                      inputType={InputType.positiveInteger}
                    />
                  </FormRow>

                  <ImageUploadField<FormValues>
                    fieldName="imageFile"
                    label="Upload image"
                    acceptedFileTypes={imageFileTypes}
                    width={160}
                    height={160}
                    alt="Marketplace item preview"
                  />
                </Stack>
              </Grid>

              <Grid size={6}>
                <Stack spacing={5}>
                  <FormRow>
                    <RhfNumberInput
                      name="order"
                      label="Order"
                      control={control}
                      inputType={InputType.positiveInteger}
                    />
                  </FormRow>

                  <FormRow position="relative" left={-16}>
                    <CheckboxElement
                      name="isExclusive"
                      label="Exclusive"
                      control={control}
                      labelProps={{
                        labelPlacement: 'start',
                      }}
                    />
                  </FormRow>
                </Stack>
              </Grid>
            </Grid>
          </FormProvider>

          <FormRow width={420} mt={3}>
            <Button
              type="button"
              variant="outlined"
              onClick={() => {
                navigate(-1);
              }}
              sx={{flex: 1, border: '0.4px solid #CBCDD8 !important'}}
            >
              Back
            </Button>

            <Button
              type="submit"
              sx={{flex: 2}}
              disabled={isUploadingFile || isCreatingItem}
            >
              Create item
            </Button>
          </FormRow>
        </form>
      </CardBox>
    </>
  );
};
